import { IFacebookLikeWidgetData, IFacebookLikeWidgetStyle } from "@pageBuilder/typings";
import { WidgetRendererBase } from "./widget-renderer-base";

export class FacebookLikeWidgetRenderer extends WidgetRendererBase<IFacebookLikeWidgetData> {

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("facebookLikeWidget");
    }

    protected renderInternal(element: HTMLElement, data: IFacebookLikeWidgetData): HTMLElement {
        this.clearAlignmentClasses(element);

        let content = element.querySelector(".widget-content") as HTMLElement;
        if (!content) {
            content = this.createContentElement();
            element.appendChild(content);
        }

        const facebookLikeElement = this.ensureFacebookElement(content);

        if (this.isEditorRendering) {
            this.renderPlaceholderInternal(facebookLikeElement, data);
            content.appendChild(facebookLikeElement);
        } else {
            content.innerHTML = this.renderFbLikeSnippet(element, data);
        }

        this.renderStyles(element, data);

        return element;
    }

    private clearAlignmentClasses(element: HTMLElement): void {
        element.classList.remove("alignable", "justify-start", "justify-center", "justify-end");
    }

    private ensureFacebookElement(element: HTMLElement): HTMLElement {
        const facebookLikeElement = element.querySelector(".fb-standard-wrapper, .fb-button-wrapper, .fb-boxed-button-wrapper") as HTMLElement;
        if (!facebookLikeElement) {
            return document.createElement("div");
        }

        return facebookLikeElement;
    }

    protected renderPlaceholderInternal(facebookLikeElement: HTMLElement, data: IFacebookLikeWidgetData): void {
        while (facebookLikeElement.firstChild) {
            facebookLikeElement.removeChild(facebookLikeElement.firstChild);
        }
        facebookLikeElement.className = "";

        const likeIcon = "https://tvdriveprod.blob.core.windows.net/drive-public-beef6b2751fc4b3e8fcb29a966397173/dd5b656e7c2c43debb6cce0b75686adf_facebook-like.png";
        const facebookIcon = "https://tvdriveprod.blob.core.windows.net/drive-public-beef6b2751fc4b3e8fcb29a966397173/7e01450c4f5045099317bc03a6fca50d_facebook-recommend.png";
        const button = document.createElement("button");

        button.classList.add("fb-button");
        button.innerHTML = data.action === "like" ?
            `<img class="like-icon" src="${likeIcon}" alt="like"/><span class='btn-label'>Like</span>` :
            `<img class="facebook-icon" src="${facebookIcon}" alt="recommend"/><span class='btn-label'>Recommend</span>`;

        const shareButton = document.createElement("button");
        shareButton.classList.add("fb-button", "share");
        shareButton.innerHTML = "<span class='btn-label'>Share</span>";

        const spanText = document.createElement("span");
        spanText.classList.add("fb-standard-label");
        spanText.textContent = data.action === "like" ? "Be the first of your friends to like this." : "Be the first of your friends to recommend this.";

        if (data.layout === "standard") {
            facebookLikeElement.classList.add("fb-standard-wrapper");
            button.classList.add("standard");
            shareButton.classList.add("standard");

            facebookLikeElement.appendChild(button);

            if (data.includeShared){
                facebookLikeElement.appendChild(shareButton);
            }
            facebookLikeElement.appendChild(spanText);
        } else if ( data.layout === "button" || data.layout === "button_count") {
            facebookLikeElement.classList.add("fb-button-wrapper");

            if (data.layout === "button_count") {
                button.innerHTML = button.innerHTML + "<span class='btn-number-label'> 0</span>";
            }
            facebookLikeElement.appendChild(button);

            if (data.includeShared){
                facebookLikeElement.appendChild(shareButton);
            }
        } else if (data.layout === "box_count") {
            facebookLikeElement.classList.add("fb-boxed-button-wrapper");

            if (data.action === "like"){
                facebookLikeElement.classList.add("like");
                button.classList.add("like");
                shareButton.classList.add("like");
            } else {
                facebookLikeElement.classList.add("recommend");
                button.classList.add("recommend");
                shareButton.classList.add("recommend");
            }

            shareButton.classList.add("for-boxed");
            button.classList.add("boxed");

            const div = document.createElement("div");
            div.classList.add("fb-counter-box");
            const span = document.createElement("span");
            span.textContent = "0";
            div.appendChild(span);

            facebookLikeElement.appendChild(div);
            facebookLikeElement.appendChild(button);

            if (data.includeShared){
                facebookLikeElement.appendChild(shareButton);
            }
        }
    }

    private renderFbLikeSnippet(element: HTMLElement, data: IFacebookLikeWidgetData): string {
        let widthAttribute = "";
        if (data.layout === "standard" && data.includeShared) {
            widthAttribute = this.calculateWidth(data.action, 310);
        } else {
            if (data.layout === "standard") {
                widthAttribute = this.calculateWidth(data.action, 250);
            }
        }

        return `<!-- Load Facebook SDK for JavaScript -->
        <div class="fb-like-wrapper ${data.layout} ${data.includeShared ? "shared" : ""}">
            <div id="fb-root"></div>
            <script async defer crossorigin="anonymous" src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0"></script>
            <!-- Your like button code -->
            <div class="fb-like"
            data-size="small"
            data-href="${data.url}"
            data-layout="${data.layout}"
            data-action="${data.action}"
            data-share="${data.includeShared}"
            ${widthAttribute}>
            </div>
        </div>`;
    }

    protected renderStyles(element: HTMLElement, data: IFacebookLikeWidgetData): void {
        this.renderBaseStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style: IFacebookLikeWidgetStyle | null): void {
        element.classList.add("alignable");
        element.classList.add(style?.alignment ? `justify-${style?.alignment}` : "justify-center");
        this.animationRenderer.setAnimationStyles(element, style?.animation);
    }

    private calculateWidth(action: string, widthValue: number): string {
        return action === "like" ? `data-width="${widthValue}"` : `data-width="${widthValue + 80}"`;
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

}

